import { useState, useEffect, useCallback, useMemo } from 'react';
import { UmsatzWert } from '@/components/UmsatzSperre';
import { shopAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  ShoppingBag, Package, TrendingUp, AlertTriangle, ClipboardList,
  Plus, Edit2, Trash2, X, Check, Search, ChevronDown, ChevronUp,
  ArrowUpDown, Eye, Ban, CheckCircle, Clock, Box, Minus,
  BarChart3, ArrowDown, ArrowUp, RefreshCw
} from 'lucide-react';

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { key: 'products', label: 'Produkte', icon: Package },
  { key: 'orders', label: 'Bestellungen', icon: ClipboardList },
  { key: 'inventory', label: 'Bestand', icon: Box },
];

const PRODUCT_CATEGORIES = [
  'allgemein', 'bekleidung', 'nahrungsergaenzung', 'zubehoer', 'getraenke', 'equipment', 'personal_training', 'ernaehrungscoaching'
];

const CATEGORY_LABELS = {
  allgemein: 'Allgemein',
  bekleidung: 'Bekleidung',
  nahrungsergaenzung: 'Nahrungsergänzung',
  zubehoer: 'Zubehör',
  getraenke: 'Getränke',
  equipment: 'Equipment',
  personal_training: 'Personal Training (PT)',
  ernaehrungscoaching: 'Ernährungscoaching',
};

const STATUS_CONFIG = {
  pending: { label: 'Ausstehend', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock },
  confirmed: { label: 'Bestätigt', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Check },
  completed: { label: 'Abgeschlossen', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle },
  cancelled: { label: 'Storniert', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: Ban },
};

const PAYMENT_LABELS = {
  bar: 'Bar',
  stripe: 'Stripe',
  rechnung: 'Rechnung',
};

function formatCurrency(val) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val || 0);
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─── KPI Card ───
function KPICard({ icon: Icon, label, value, sub, color = 'text-primary' }) {
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`} className="bg-card border border-border rounded-lg p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color === 'text-primary' ? 'bg-primary/10' : color.includes('amber') ? 'bg-amber-100 dark:bg-amber-900/30' : color.includes('emerald') ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-primary/10'}`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl font-bold mt-0.5">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Dashboard Tab ───
function DashboardTab() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    shopAPI.dashboard().then(r => setKpis(r.data)).catch(() => toast.error('Dashboard konnte nicht geladen werden')).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!kpis) return <p className="text-muted-foreground text-center py-10">Keine Daten verfügbar</p>;

  return (
    <div data-testid="shop-dashboard" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard icon={Package} label="Produkte" value={kpis.total_products} />
        <KPICard icon={ClipboardList} label="Offene Bestellungen" value={kpis.open_orders} sub={`${kpis.today_orders} heute`} color="text-amber-600" />
        <KPICard icon={TrendingUp} label="Umsatz" value={<UmsatzWert wert={kpis.shop_revenue} format={formatCurrency} />} color="text-emerald-600" />
        <KPICard icon={AlertTriangle} label="Niedriger Bestand" value={kpis.low_stock_count} sub={`${kpis.active_reservations} Reservierungen`} color="text-amber-600" />
      </div>
      <div className="bg-card border border-border rounded-lg p-5">
        <h3 className="text-sm font-semibold mb-1">Übersicht</h3>
        <p className="text-xs text-muted-foreground">Gesamt-Bestellungen: <span className="font-medium text-foreground">{kpis.total_orders}</span></p>
      </div>
    </div>
  );
}

// ─── Products Tab ───
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    shopAPI.listProducts({ active_only: false }).then(r => setProducts(r.data.products || [])).catch(() => toast.error('Fehler beim Laden')).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const filtered = useMemo(() => {
    let list = products;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(s) || p.category?.toLowerCase().includes(s));
    }
    if (catFilter) list = list.filter(p => p.category === catFilter);
    return list;
  }, [products, search, catFilter]);

  const handleDelete = async (p) => {
    if (!window.confirm(`"${p.name}" wirklich deaktivieren?`)) return;
    try {
      await shopAPI.deleteProduct(p.id);
      toast.success('Produkt deaktiviert');
      fetchProducts();
    } catch { toast.error('Fehler beim Deaktivieren'); }
  };

  return (
    <div data-testid="shop-products" className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="product-search"
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Produkt suchen..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-input bg-background"
          />
        </div>
        <select
          data-testid="category-filter"
          value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="text-sm rounded-md border border-input bg-background px-3 py-2"
        >
          <option value="">Alle Kategorien</option>
          {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
        </select>
        <button
          data-testid="add-product-btn"
          onClick={() => { setEditProduct(null); setShowForm(true); }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" /> Produkt anlegen
        </button>
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <ProductForm
          product={editProduct}
          onClose={() => { setShowForm(false); setEditProduct(null); }}
          onSaved={() => { setShowForm(false); setEditProduct(null); fetchProducts(); }}
        />
      )}

      {/* Product Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Keine Produkte gefunden</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-3 py-2.5 font-medium">Produkt</th>
                <th className="text-left px-3 py-2.5 font-medium">Kategorie</th>
                <th className="text-right px-3 py-2.5 font-medium">Netto</th>
                <th className="text-right px-3 py-2.5 font-medium">Brutto</th>
                <th className="text-center px-3 py-2.5 font-medium">Bestand</th>
                <th className="text-center px-3 py-2.5 font-medium">Verfügbar</th>
                <th className="text-center px-3 py-2.5 font-medium">Status</th>
                <th className="text-right px-3 py-2.5 font-medium">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                  <td className="px-3 py-2.5 font-medium">{p.name}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{CATEGORY_LABELS[p.category] || p.category}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{formatCurrency(p.price_net)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{formatCurrency(p.price_gross)}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums">{p.stock_quantity}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`tabular-nums font-medium ${p.low_stock ? 'text-amber-600' : 'text-emerald-600'}`}>{p.available}</span>
                    {p.reserved > 0 && <span className="text-xs text-muted-foreground ml-1">({p.reserved} res.)</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {p.active ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Aktiv</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Inaktiv</span>
                    )}
                    {p.low_stock && p.active && (
                      <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <AlertTriangle className="w-3 h-3 inline -mt-0.5" />
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`edit-product-${p.id}`}
                        onClick={() => { setEditProduct(p); setShowForm(true); }}
                        className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      {p.active && (
                        <button
                          data-testid={`delete-product-${p.id}`}
                          onClick={() => handleDelete(p)}
                          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors text-muted-foreground hover:text-red-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Product Form ───
function ProductForm({ product, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: product?.name || '',
    category: product?.category || 'allgemein',
    description: product?.description || '',
    price_net: product?.price_net ?? '',
    tax_rate: product?.tax_rate ?? 0.19,
    stock_quantity: product?.stock_quantity ?? 0,
    min_stock: product?.min_stock ?? 2,
    active: product?.active ?? true,
    coaching_wochen: product?.coaching_wochen ?? 12,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name ist erforderlich');
    if (form.price_net === '' || isNaN(form.price_net)) return toast.error('Preis ist erforderlich');

    setSaving(true);
    try {
      const payload = { ...form, price_net: parseFloat(form.price_net), stock_quantity: parseInt(form.stock_quantity) || 0, min_stock: parseInt(form.min_stock) || 2 };
      // Laufzeit nur für Ernährungscoaching (10.10.2026); bei anderen Kategorien nicht mitschicken
      if (form.category === 'ernaehrungscoaching') payload.coaching_wochen = parseInt(form.coaching_wochen) || 12;
      else delete payload.coaching_wochen;
      if (product) {
        await shopAPI.updateProduct(product.id, payload);
        toast.success('Produkt aktualisiert');
      } else {
        await shopAPI.createProduct(payload);
        toast.success('Produkt angelegt');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const grossPrice = form.price_net !== '' ? (parseFloat(form.price_net) * (1 + form.tax_rate)).toFixed(2) : '—';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-base font-semibold">{product ? 'Produkt bearbeiten' : 'Neues Produkt'}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="product-form">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Name *</label>
            <input data-testid="product-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Kategorie</label>
              <select data-testid="product-category" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background">
                {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">MwSt-Satz</label>
              <select data-testid="product-tax" value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) }))}
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background">
                <option value={0.19}>19%</option>
                <option value={0.07}>7%</option>
                <option value={0}>0%</option>
              </select>
            </div>
          </div>
          {form.category === 'ernaehrungscoaching' && (
            <div>
              <label htmlFor="product-coaching-wochen" className="text-xs font-medium text-muted-foreground">Laufzeit (Wochen)</label>
              <input id="product-coaching-wochen" data-testid="product-coaching-wochen" type="number" min="1" max="104" value={form.coaching_wochen}
                onChange={e => setForm(f => ({ ...f, coaching_wochen: e.target.value }))}
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background" />
              <p className="text-xs text-muted-foreground mt-1">Nach der Bestätigung der Bestellung übernimmt ein Coach unter Ernährung › Buchungen; ab dem Erstgespräch läuft die Laufzeit. Bestand großzügig setzen (zählt wie beim PT).</p>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Beschreibung</label>
            <textarea data-testid="product-description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Netto-Preis (EUR) *</label>
              <input data-testid="product-price" type="number" step="0.01" min="0" value={form.price_net}
                onChange={e => setForm(f => ({ ...f, price_net: e.target.value }))}
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background" />
              <p className="text-xs text-muted-foreground mt-1">Brutto: {grossPrice !== '—' ? formatCurrency(parseFloat(grossPrice)) : '—'}</p>
            </div>
            {!product && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Anfangsbestand</label>
                <input data-testid="product-stock" type="number" min="0" value={form.stock_quantity}
                  onChange={e => setForm(f => ({ ...f, stock_quantity: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Mindestbestand</label>
              <input data-testid="product-min-stock" type="number" min="0" value={form.min_stock}
                onChange={e => setForm(f => ({ ...f, min_stock: e.target.value }))}
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
            <label className="text-sm">Aktiv</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-input hover:bg-muted transition-colors">Abbrechen</button>
            <button data-testid="product-save-btn" type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors">
              {saving ? 'Speichere...' : (product ? 'Speichern' : 'Anlegen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Orders Tab ───
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    const params = statusFilter ? { status: statusFilter } : {};
    shopAPI.listOrders(params).then(r => setOrders(r.data.orders || [])).catch(() => toast.error('Fehler beim Laden')).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleAction = async (orderId, action) => {
    try {
      if (action === 'confirm') { await shopAPI.confirmOrder(orderId); toast.success('Bestellung bestätigt'); }
      else if (action === 'complete') { await shopAPI.completeOrder(orderId); toast.success('Bestellung abgeschlossen'); }
      else if (action === 'cancel') {
        if (!window.confirm('Bestellung wirklich stornieren?')) return;
        await shopAPI.cancelOrder(orderId);
        toast.success('Bestellung storniert');
      }
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  return (
    <div data-testid="shop-orders" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
        <select data-testid="order-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-sm rounded-md border border-input bg-background px-3 py-2">
          <option value="">Alle Status</option>
          <option value="pending">Ausstehend</option>
          <option value="confirmed">Bestätigt</option>
          <option value="completed">Abgeschlossen</option>
          <option value="cancelled">Storniert</option>
        </select>
        <div className="flex-1" />
        <button data-testid="create-order-btn" onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
          <Plus className="w-4 h-4" /> Neue Bestellung
        </button>
      </div>

      {showCreate && <OrderCreateForm onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchOrders(); }} />}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Keine Bestellungen gefunden</p>
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map(order => {
            const st = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const StIcon = st.icon;
            return (
              <div key={order.id} data-testid={`order-${order.id}`} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold font-mono">{order.id}</span>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${st.color}`}>
                        <StIcon className="w-3 h-3" />{st.label}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{PAYMENT_LABELS[order.payment_method] || order.payment_method}</span>
                    </div>
                    {order.member_name && <p className="text-xs text-muted-foreground mt-1">Kunde: {order.member_name}</p>}
                    <div className="mt-2 space-y-0.5">
                      {order.items?.map((item, i) => (
                        <p key={`${order.id}-item-${i}`} className="text-xs text-muted-foreground">
                          {item.quantity}x {item.product_name} — {formatCurrency(item.line_total_gross)}
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-base font-bold">{formatCurrency(order.total_gross)}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(order.created_at)}</p>
                    <div className="flex gap-1 mt-2 justify-end">
                      {order.status === 'pending' && (
                        <>
                          <button data-testid={`confirm-order-${order.id}`} onClick={() => handleAction(order.id, 'confirm')}
                            className="p-1.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 transition-colors" title="Bestätigen">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`cancel-order-${order.id}`} onClick={() => handleAction(order.id, 'cancel')}
                            className="p-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 transition-colors" title="Stornieren">
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      {order.status === 'confirmed' && (
                        <>
                          <button data-testid={`complete-order-${order.id}`} onClick={() => handleAction(order.id, 'complete')}
                            className="p-1.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 transition-colors" title="Abschließen">
                            <CheckCircle className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`cancel-order-${order.id}`} onClick={() => handleAction(order.id, 'cancel')}
                            className="p-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 transition-colors" title="Stornieren">
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {order.notes && <p className="text-xs text-muted-foreground mt-2 italic">Notiz: {order.notes}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Order Create Form ───
function OrderCreateForm({ onClose, onCreated }) {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [memberName, setMemberName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bar');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    shopAPI.listProducts({ active_only: true }).then(r => setProducts(r.data.products || [])).catch((e) => { console.error('Produkte laden:', e); });
  }, []);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(c => c.product_id === product.id);
      if (existing) {
        return prev.map(c => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, { product_id: product.id, product_name: product.name, quantity: 1, price_gross: product.price_gross, available: product.available }];
    });
  };

  const updateQty = (pid, delta) => {
    setCart(prev => prev.map(c => {
      if (c.product_id !== pid) return c;
      const newQty = c.quantity + delta;
      if (newQty < 1) return c;
      return { ...c, quantity: newQty };
    }));
  };

  const removeFromCart = (pid) => setCart(prev => prev.filter(c => c.product_id !== pid));

  const total = cart.reduce((sum, c) => sum + c.price_gross * c.quantity, 0);

  const handleSubmit = async () => {
    if (cart.length === 0) return toast.error('Warenkorb ist leer');
    setSaving(true);
    try {
      await shopAPI.createOrder({
        items: cart.map(c => ({ product_id: c.product_id, quantity: c.quantity })),
        member_name: memberName,
        payment_method: paymentMethod,
        notes,
      });
      toast.success('Bestellung erstellt');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-base font-semibold">Neue Bestellung</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4" data-testid="order-form">
          {/* Customer */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Kundenname</label>
              <input data-testid="order-member-name" value={memberName} onChange={e => setMemberName(e.target.value)}
                placeholder="Optional" className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Zahlungsart</label>
              <select data-testid="order-payment" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background">
                <option value="bar">Bar</option>
                <option value="stripe">Stripe</option>
                <option value="rechnung">Rechnung</option>
              </select>
            </div>
          </div>
          {/* Product Select */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Produkt hinzufügen</label>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
              {products.map(p => (
                <button key={p.id} data-testid={`add-to-cart-${p.id}`} onClick={() => addToCart(p)}
                  disabled={p.available < 1}
                  className="text-left p-2 rounded-md border border-input hover:border-primary/50 hover:bg-muted/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <p className="text-xs font-medium truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{formatCurrency(p.price_gross)} · {p.available} verf.</p>
                </button>
              ))}
            </div>
          </div>
          {/* Cart */}
          {cart.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">Warenkorb</div>
              {cart.map(c => (
                <div key={c.product_id} className="flex items-center gap-2 px-3 py-2 border-t border-border">
                  <span className="flex-1 text-sm truncate">{c.product_name}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQty(c.product_id, -1)} className="p-0.5 rounded hover:bg-muted"><Minus className="w-3 h-3" /></button>
                    <span className="text-sm font-medium w-6 text-center tabular-nums">{c.quantity}</span>
                    <button onClick={() => updateQty(c.product_id, 1)} className="p-0.5 rounded hover:bg-muted"><Plus className="w-3 h-3" /></button>
                  </div>
                  <span className="text-sm font-medium w-20 text-right tabular-nums">{formatCurrency(c.price_gross * c.quantity)}</span>
                  <button onClick={() => removeFromCart(c.product_id)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-muted-foreground hover:text-red-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/20">
                <span className="text-sm font-semibold">Gesamt</span>
                <span className="text-base font-bold">{formatCurrency(total)}</span>
              </div>
            </div>
          )}
          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Notiz</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-input hover:bg-muted transition-colors">Abbrechen</button>
            <button data-testid="order-submit-btn" onClick={handleSubmit} disabled={saving || cart.length === 0}
              className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors">
              {saving ? 'Erstelle...' : 'Bestellung aufgeben'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Inventory Tab ───
function InventoryTab() {
  const [lowStock, setLowStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCorrection, setShowCorrection] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [lowRes, movRes] = await Promise.all([
        shopAPI.getLowStock(),
        shopAPI.listMovements({ limit: 50 }),
      ]);
      setLowStock(lowRes.data.products || []);
      setMovements(movRes.data.movements || []);
    } catch { toast.error('Fehler beim Laden'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div data-testid="shop-inventory" className="space-y-6">
      {/* Stock Correction Modal */}
      {showCorrection && (
        <StockCorrectionForm
          product={showCorrection}
          onClose={() => setShowCorrection(null)}
          onSaved={() => { setShowCorrection(null); fetchData(); }}
        />
      )}

      {/* Low Stock Warnings */}
      <div>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-500" /> Niedriger Bestand
        </h3>
        {loading ? (
          <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : lowStock.length === 0 ? (
          <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/50 rounded-lg p-4 text-sm text-emerald-700 dark:text-emerald-400">
            Alle Produkte haben ausreichend Bestand.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {lowStock.map(p => (
              <div key={p.id} data-testid={`low-stock-${p.id}`} className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{p.name}</span>
                  <button data-testid={`stock-correct-${p.id}`} onClick={() => setShowCorrection(p)}
                    className="p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                    <ArrowUpDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                  Verfügbar: <span className="font-bold">{p.available}</span> / Min: {p.min_stock}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stock Movements */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Letzte Bestandsbewegungen
          </h3>
          <button onClick={fetchData} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Aktualisieren</button>
        </div>
        {movements.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">Keine Bewegungen vorhanden</p>
        ) : (
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-3 py-2 font-medium">Datum</th>
                  <th className="text-left px-3 py-2 font-medium">Produkt</th>
                  <th className="text-left px-3 py-2 font-medium">Typ</th>
                  <th className="text-right px-3 py-2 font-medium">Menge</th>
                  <th className="text-left px-3 py-2 font-medium">Grund</th>
                </tr>
              </thead>
              <tbody>
                {movements.map(m => (
                  <tr key={m.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 text-xs text-muted-foreground">{formatDate(m.created_at)}</td>
                    <td className="px-3 py-2 font-medium">{m.product_name}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-xs ${m.quantity > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {m.quantity > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                        {m.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      <span className={m.quantity > 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {m.quantity > 0 ? `+${m.quantity}` : m.quantity}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{m.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Stock Correction Form ───
function StockCorrectionForm({ product, onClose, onSaved }) {
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('Manuelle Korrektur');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseInt(quantity);
    if (!qty || qty === 0) return toast.error('Menge eingeben (positiv = Zugang, negativ = Abgang)');
    setSaving(true);
    try {
      await shopAPI.correctStock(product.id, { quantity: qty, reason });
      toast.success('Bestand korrigiert');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-base font-semibold">Bestandskorrektur: {product.name}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="stock-correction-form">
          <p className="text-xs text-muted-foreground">Aktueller Bestand: <span className="font-bold text-foreground">{product.stock_quantity}</span></p>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Menge (+/−)</label>
            <input data-testid="correction-quantity" type="number" value={quantity} onChange={e => setQuantity(e.target.value)}
              placeholder="z.B. 10 oder -5" className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Grund</label>
            <input data-testid="correction-reason" value={reason} onChange={e => setReason(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-input bg-background" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-input hover:bg-muted transition-colors">Abbrechen</button>
            <button data-testid="correction-save-btn" type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors">
              {saving ? 'Speichere...' : 'Korrigieren'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Shop Page ───
export default function ShopPage() {
  const [tab, setTab] = useState('dashboard');

  return (
    <div data-testid="shop-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-primary" /> Shop & Warenwirtschaft
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Produkte, Bestand, Bestellungen verwalten</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`shop-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                tab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'products' && <ProductsTab />}
      {tab === 'orders' && <OrdersTab />}
      {tab === 'inventory' && <InventoryTab />}
    </div>
  );
}
